import { tracker } from '@lessonup/analytics';
import { decodeYjsPins, tipTapContentToText } from '@lessonup/editor-shared';
import { UpdatePinDispatch } from '@lessonup/pin-renderer';
import {
  isTextPinComponent,
  newQuizPin,
  Pin,
  PinCreationParams,
  QuizPinAnswerProps,
  QuizPinLayoutTemplate,
  QuizTeacherPin,
  stringToProsemirror,
  TeacherPin,
} from '@lessonup/pins-shared';
import { Doc as YDoc } from 'yjs';
import { showQuizModal } from '../../ChatGPTGenerator/ChatGPTGeneratorFeature';
import { GeneratedLessonResponse, QuizResponse } from '../../ChatGPTGenerator/model';
import { getQuestionComponentInInteractivePin } from './pin/pin.utils';

export const maiaCreateV2QuizPin = (
  quiz: QuizResponse['list'][0],
  layout: QuizPinLayoutTemplate
): PinCreationParams<QuizTeacherPin> => {
  const answers = maiaV2QuizResponseAnswersToPinAnswers(quiz.answers);
  const correctAnswers = maiaV2QuizResponseCorrectAnswersToCorrectPinAnswers(quiz.correctAnswer, answers);
  const pin = newQuizPin(layout, correctAnswers, answers);
  const questionComponent = getQuestionComponentInInteractivePin(pin);
  questionComponent.settings.tipTapContent = maiaQuestionToTipTapContent(quiz.question);
  return pin;
};

const maiaV2QuizResponseAnswersToPinAnswers = (answers: string[]): QuizPinAnswerProps[] => {
  return answers.map((answer, i) => ({ type: 'TEXT', id: answer + i, text: answer }));
};

const maiaV2QuizResponseCorrectAnswersToCorrectPinAnswers = (
  correctAnswers: number[],
  answers: QuizPinAnswerProps[]
): string[] => {
  return correctAnswers.map((correctAnswer) => answers[correctAnswer - 1].id);
};

const maiaQuestionToTipTapContent = (question: string) =>
  stringToProsemirror(question, { fontSize: '32px', fontFamily: 'Plus Jakarta Sans', bold: true });

export const pinsContentToString = (yDoc: YDoc, pins: Pin[]): string => {
  const decodedPins = decodeYjsPins(yDoc, pins);
  const pinComponents = decodedPins
    .map((pin) => pin.pinComponents)
    .flat()
    .filter((c) => c.type === 'TEXT');

  const textPinComponents = pinComponents.filter(isTextPinComponent);
  return textPinComponents
    .map((c) => {
      const content = c.settings.tipTapContent ? tipTapContentToText(c.settings.tipTapContent) : '';
      return content;
    })
    .join(' ');
};

export const generateMaiaQuiz = (
  afterPinId: string | undefined,
  layout: QuizPinLayoutTemplate,
  dispatch: UpdatePinDispatch,
  onAddNewPins: (pinIds: string[]) => void,
  lessonId: string,
  prompt?: string
) => {
  showQuizModal({
    onGenerate: (res) => {
      res.list.reverse().map((quiz) => {
        //Current pin creation methods don't support unique id's for object reference so we dispatch for each pin we create rather then batch them
        const pin = maiaCreateV2QuizPin(quiz, layout);
        dispatch({
          type: 'addPins',
          afterPinId: afterPinId,
          pins: [pin],
          onComplete: onAddNewPins,
        });
        tracker.events.pinCreate({
          lessonId,
          pinType: 'quiz',
          pinCreationMethod: 'generate_with_ai',
          editorVersion: 'v2',
        });
      });
    },
    prompt,
  });
};

export const handleGeneratedLessonResponse = (response: GeneratedLessonResponse): TeacherPin[] => {
  return response.list.map((item) => {
    return item.pin;
  });
};
